/**
 * CraftingSystem - Manages recipes and crafting
 */

import { Recipe, RECIPES } from './Recipe';
import { InventoryComponent } from '../items/InventoryComponent';
import { ItemRegistry } from '../items/ItemRegistry';
import { ItemType } from '../types/enums';

export interface CraftResult {
  success: boolean;
  message: string;
}

export class CraftingSystem {
  private recipes: Map<string, Recipe> = new Map();
  private unlockedRecipes: Set<string> = new Set();

  constructor() {
    this.registerDefaultRecipes();
  }

  private registerDefaultRecipes(): void {
    for (const recipe of RECIPES) {
      this.registerRecipe(recipe);
      // Unlock all recipes by default (building workbench is the gate for advanced recipes)
      this.unlockedRecipes.add(recipe.id);
    }
  }

  /** Register a recipe */
  registerRecipe(recipe: Recipe): void {
    this.recipes.set(recipe.id, recipe);
  }

  /** Get recipe by ID */
  getRecipe(id: string): Recipe | undefined {
    return this.recipes.get(id);
  }

  /** Get all recipes */
  getAllRecipes(): Recipe[] {
    return Array.from(this.recipes.values());
  }

  /** Get unlocked recipes */
  getUnlockedRecipes(): Recipe[] {
    return this.getAllRecipes().filter(r => this.unlockedRecipes.has(r.id));
  }

  /** Get recipes available at a station (or by hand if null) */
  getRecipesForStation(station: string | null): Recipe[] {
    return this.getUnlockedRecipes().filter(r => r.requiredStation === station);
  }

  /** Get recipes by category */
  getRecipesByCategory(category: ItemType): Recipe[] {
    return this.getUnlockedRecipes().filter(r => r.category === category);
  }

  /** Unlock a recipe */
  unlockRecipe(recipeId: string): void {
    this.unlockedRecipes.add(recipeId);
  }

  /** Check if recipe is unlocked */
  isRecipeUnlocked(recipeId: string): boolean {
    return this.unlockedRecipes.has(recipeId);
  }

  /** Check if can craft a recipe */
  canCraft(recipe: Recipe, inventory: InventoryComponent, currentStation: string | null = null): boolean {
    // Check station requirement
    if (recipe.requiredStation && recipe.requiredStation !== currentStation) {
      return false;
    }

    // Check all ingredients
    for (const ingredient of recipe.ingredients) {
      if (!inventory.hasItem(ingredient.itemId, ingredient.quantity)) {
        return false;
      }
    }

    return true;
  }

  /** Get missing ingredients for a recipe */
  getMissingIngredients(recipe: Recipe, inventory: InventoryComponent): { itemId: string; needed: number; have: number }[] {
    const missing: { itemId: string; needed: number; have: number }[] = [];

    for (const ingredient of recipe.ingredients) {
      const have = inventory.getItemCount(ingredient.itemId);
      if (have < ingredient.quantity) {
        missing.push({
          itemId: ingredient.itemId,
          needed: ingredient.quantity,
          have
        });
      }
    }

    return missing;
  }

  /** Craft a recipe */
  craft(recipe: Recipe, inventory: InventoryComponent, currentStation: string | null = null): CraftResult {
    if (!this.canCraft(recipe, inventory, currentStation)) {
      return { success: false, message: 'Missing requirements' };
    }

    // Remove ingredients
    for (const ingredient of recipe.ingredients) {
      inventory.removeItem(ingredient.itemId, ingredient.quantity);
    }

    // Add result
    const resultItem = ItemRegistry.get(recipe.result.itemId);
    if (!resultItem) {
      return { success: false, message: 'Unknown item' };
    }

    const overflow = inventory.addItem(resultItem, recipe.result.quantity);
    if (overflow > 0) {
      // TODO: Drop on ground
      console.log(`Dropped ${overflow} items (inventory full)`);
    }

    return { success: true, message: `Crafted ${recipe.name}` };
  }
}
